/**
 * Button Component — Yoluno design system
 *
 * Pills for primary/secondary/outline/destructive.
 * Ghost uses var(--radius-lg) (softer, not full pill).
 * Spring transition and hover/press motion via .btn-spring CSS class.
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base: pill, Nunito bold, spring motion, focus ring
  [
    'btn-spring',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-body font-bold text-[15px] cursor-pointer',
    'rounded-[var(--radius-pill)] border border-transparent',
    'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // Teal pill + brand shadow — lifts on hover (handled by .btn-spring)
        default:
          'bg-[var(--brand)] text-white shadow-[var(--shadow-brand)] hover:bg-[var(--brand-strong)]',
        // Gold outline pill
        gold:
          'border-2 border-[var(--accent-loti)] text-[var(--accent-loti)] bg-transparent hover:bg-[var(--accent-loti)] hover:text-white',
        // Destructive
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        // White pill with border — secondary action
        outline:
          'bg-[var(--surface-card)] text-[var(--text-strong)] border-[var(--border-default)] shadow-[var(--shadow-sm)] hover:border-[var(--border-brand)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand-strong)]',
        // Tinted teal — tertiary
        secondary:
          'bg-[var(--brand-soft)] text-[var(--brand-strong)] hover:bg-[var(--teal-100)]',
        // Ghost — softer radius, no border/bg
        ghost:
          'rounded-[var(--radius-lg)] border-transparent hover:bg-[var(--brand-soft)] hover:text-[var(--brand-strong)]',
        // Link
        link: 'text-[var(--text-link)] underline-offset-4 hover:underline rounded-none border-none shadow-none',
      },
      size: {
        default: 'h-12 px-8 py-3',
        sm:      'h-10 px-5 py-2 text-sm',
        lg:      'h-14 px-10 py-4 text-base',
        icon:    'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
